import { useEffect, useRef } from 'react';
import { burstParticleCount, getMotionTier, type BurstKind } from '@/lib/motion-effects';

type Particle = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  r: number;
  life: number;
  maxLife: number;
  hue: number;
};

function createParticles(x: number, y: number, count: number, kind: BurstKind): Particle[] {
  return Array.from({ length: count }, () => {
    const angle = Math.random() * Math.PI * 2;
    const speed = kind === 'coin' ? 1.2 + Math.random() * 2 : 2 + Math.random() * 4;
    return {
      x,
      y,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed - (kind === 'coin' ? 0.5 : 2.5),
      r: kind === 'celebration' ? 5 + Math.random() * 14 : 3 + Math.random() * 10,
      life: 0,
      maxLife: kind === 'celebration' ? 700 : 550,
      hue: kind === 'coin' ? 42 : Math.random() > 0.3 ? 78 : 42,
    };
  });
}

type ParticleBurstProps = {
  x: number;
  y: number;
  kind: BurstKind;
  onDone: () => void;
};

export function ParticleBurst({ x, y, kind, onDone }: ParticleBurstProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const doneRef = useRef(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      onDone();
      return;
    }

    const tier = getMotionTier();
    const count = burstParticleCount(tier, kind);
    if (count === 0) {
      onDone();
      return;
    }

    const size = kind === 'celebration' ? 220 : 140;
    canvas.width = size;
    canvas.height = size;
    canvas.style.width = `${size}px`;
    canvas.style.height = `${size}px`;
    canvas.style.left = `${x - size / 2}px`;
    canvas.style.top = `${y - size / 2}px`;

    const originX = size / 2;
    const originY = size / 2;
    const particles = createParticles(originX, originY, count, kind);
    let raf = 0;
    let start = 0;

    const frame = (ts: number) => {
      if (!start) start = ts;
      const elapsed = ts - start;
      ctx.clearRect(0, 0, size, size);

      let alive = 0;
      for (const p of particles) {
        p.life += 16;
        if (p.life > p.maxLife) continue;
        alive++;

        p.x += p.vx;
        p.y += p.vy;
        p.vy += kind === 'coin' ? 0.08 : -0.04;
        p.vx *= 0.98;

        const t = 1 - p.life / p.maxLife;
        const alpha = t * (kind === 'celebration' ? 0.55 : 0.45);

        const grad = ctx.createRadialGradient(p.x - p.r * 0.25, p.y - p.r * 0.25, 0, p.x, p.y, p.r);
        grad.addColorStop(0, `hsla(${p.hue}, 90%, 75%, ${alpha})`);
        grad.addColorStop(1, `hsla(${p.hue}, 70%, 45%, 0)`);

        ctx.beginPath();
        ctx.fillStyle = grad;
        ctx.arc(p.x, p.y, p.r * (0.6 + t * 0.5), 0, Math.PI * 2);
        ctx.fill();
      }

      if (alive === 0 || elapsed > 800) {
        if (!doneRef.current) {
          doneRef.current = true;
          onDone();
        }
        return;
      }
      raf = requestAnimationFrame(frame);
    };

    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, [x, y, kind, onDone]);

  return <canvas ref={canvasRef} className="pointer-events-none absolute" aria-hidden />;
}
